import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/data/products";
import { INDUSTRIES, PERSONAL_USES } from "@/data/site";
import { usePlatform } from "@/context/PlatformContext";
import { Reveal } from "@/components/Reveal";
import { PrimaryButton, SecondaryButton, SectionHeader } from "@/components/Primitives";
import PinDropHeader from "@/components/PinDropHeader";

const Split = ({ id, eyebrow, title, desc, chips, cta, to, img, alt, flip = false, note }) => (
  <section id={id} data-testid={`${id}-section`} className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
    <div className={`mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 lg:grid-cols-12 ${flip ? "" : ""}`}>
      <Reveal className={`lg:col-span-7 ${flip ? "lg:order-2" : ""}`}>
      <div className="relative overflow-hidden rounded-[2rem] shadow-lift">
        <img src={img} alt={alt} className="aspect-[4/3] w-full object-cover" loading="lazy" />
      </div>
      </Reveal>
      <div className={`lg:col-span-5 ${flip ? "lg:order-1" : ""}`}>
        <PinDropHeader eyebrow={eyebrow} title={title} desc={desc} />
        <Reveal delay={0.1}>
          <ul className="mt-6 flex flex-wrap gap-2">
            {chips.map((c) => (
              <li key={c} className="rounded-full border border-black/10 bg-white px-3.5 py-1.5 text-xs font-semibold text-tracksy-ink">{c}</li>
            ))}
          </ul>
          <div className="mt-8">
            <PrimaryButton to={to} testId={`${id}-cta`}>{cta}</PrimaryButton>
          </div>
          {note && <p className="mt-5 max-w-md text-xs leading-relaxed text-tracksy-muted">{note}</p>}
        </Reveal>
      </div>
    </div>
  </section>
);

export const VehiclesSection = () => {
  const { config } = usePlatform();
  return (
    <Split
      id="vehicles"
      eyebrow="Tracksy Vehicle"
      title="Made for Vehicles."
      desc="Whether it's your personal vehicle, work ute or trailer, Tracksy gives you a simple way to add tracking to the things that move."
      chips={["Work utes", "Cars", "Vans", "Trailers", "Motorbikes", "Caravans", "Boats"]}
      cta="Shop Vehicle Trackers"
      to="/vehicles"
      img={IMAGES.heroUte}
      alt="Tracksy magnetic tracker being attached inside the wheel arch of a work ute"
      note={`Unlimited range worldwide via ${config.network} — zero subscriptions, zero cell bills.`}
    />
  );
};

export const EquipmentSection = () => (
  <Split
    id="equipment"
    eyebrow="Tracksy Equipment"
    title="Tools, equipment and valuable assets."
    desc="Generators, toolboxes, hire gear and the kit that moves from site to site. Compact enough to tuck inside a lid, magnetic enough to stay put on a frame."
    chips={["Toolboxes", "Generators", "Hire equipment", "Plant trailers", "Site assets"]}
    cta="Shop Equipment Trackers"
    to="/equipment"
    img={IMAGES.generator}
    alt="Tracksy magnetic tracker attached to the frame of a portable generator"
    flip
  />
);

export const FleetSection = () => {
  const { config } = usePlatform();
  const phone = config.id === "android" ? "Android phone" : "iPhone or iPad";
  return (
  <section id="fleet" data-testid="fleet-section" className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
    <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-tracksy-dark shadow-lift">
      <img src={IMAGES.fleetVans} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-40" />
      <div className="absolute inset-0 bg-gradient-to-r from-tracksy-dark via-tracksy-dark/90 to-tracksy-dark/30" />
      <div className="relative grid grid-cols-1 gap-10 px-6 py-14 sm:px-10 lg:grid-cols-12 lg:px-16 lg:py-20">
        <div className="lg:col-span-7">
          <SectionHeader dark eyebrow="Tracksy Fleet" title="Track Your Fleet." desc="Simple tracking for vehicles, trailers and valuable business equipment. Whether you manage 3 vehicles or 100, Tracksy gives you a simple way to equip more of the assets that matter." />
          <Reveal delay={0.1}>
            <ul className="mt-6 flex flex-wrap gap-2">
              {["Tradies", "Construction", "Plumbers", "Electricians", "Landscapers", "Delivery", "Rental", "Small fleets"].map((c) => (
                <li key={c} className="rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-semibold text-white/85">{c}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-4">
              <PrimaryButton to="/fleet" testId="fleet-section-cta">Track your fleet</PrimaryButton>
              <SecondaryButton to="/shop" dark testId="fleet-section-pricing-cta">Buy any quantity</SecondaryButton>
            </div>
            <p className="mt-5 max-w-lg text-xs leading-relaxed text-white/50">
              Every Tracksy is managed through the {config.network} app on an {phone}. Unlimited range worldwide — equip 3 vehicles or 100 and find them anywhere.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 gap-3 self-center lg:col-span-5">
          {INDUSTRIES.slice(0, 6).map((ind, i) => (
            <Reveal key={ind.id} delay={i * 0.05}>
              <Link to="/fleet" className="block rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-xl transition-colors hover:border-tracksy-orange/50">
                <p className="font-heading text-sm font-bold text-white">{ind.name}</p>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-white/60">{ind.desc}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
  );
};

export const PersonalSection = () => (
  <section id="personal" data-testid="personal-section" className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
    <div className="mx-auto max-w-7xl">
      <div className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <SectionHeader eyebrow="More than just vehicles" title="One Tracker. Plenty of Uses." desc="Luggage, bikes, backpacks, toolboxes, golf and camping gear — the same tag, wherever your week takes you." />
        <Reveal>
          <Link to="/personal" data-testid="personal-section-cta" className="inline-flex items-center gap-2 text-sm font-bold text-tracksy-orange hover:underline">
            Explore Tracksy Personal <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {PERSONAL_USES.map((u, i) => (
          <Reveal key={u.label} delay={i * 0.05}>
            <figure className="group relative overflow-hidden rounded-[1.5rem] shadow-subtle">
              <img src={u.img} alt={`Tracksy used on ${u.label.toLowerCase()}`} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <figcaption className="absolute bottom-3 left-3 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-tracksy-ink backdrop-blur-xl">{u.label}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
